import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@diamond/shared/ui';
import { ContactSalesModal, QuantityControl } from '@diamond/sol-buyer-components';
import { BookmarkAdd, BookmarkAdded } from '@mui/icons-material';
import { useState } from 'react';

import { PriceRow, PromoVoucherSections } from './OrderCounterParts';
import {
  type OrderQuantityCounterProps,
  useOrderCounter,
} from './useOrderCounter';

export const OrderQuantityCounter = (props: OrderQuantityCounterProps) => {
  const hook = useOrderCounter(props, 'desktop');
  const {
    productId,
    wishlistId,
    handleAddToWishlist,
    cartTotalData,
    isCartTotalFetching,
    productData,
  } = props;

  return (
    <Box display={{ base: 'none', md: 'flex' }} justifyContent="flex-end">
      <Box w="full" minW="360px">
        {/* Card */}
        <Box shadow="base" w="full" rounded="xl" p={6} mb={4}>
          <Text fontSize="2xl" fontWeight="bold" mb={4}>
            Jumlah Pesanan
          </Text>

          <Flex align="center" justify="space-between" mb={4}>
            <Text color={hook.statusColor} fontWeight="semibold" flex={1} pr={4}>
              {hook.statusLabel}
            </Text>
            {hook.showCart && (
              <Box w="160px" flexShrink={0}>
                <QuantityControl
                  variant="add-to-cart"
                  hasFractionalStep={hook.hasFractionalStep}
                  inputProps={hook.inputProps}
                  incProps={hook.incProps}
                  decProps={hook.decProps}
                  isLoading={isCartTotalFetching}
                />
              </Box>
            )}
          </Flex>

          {hook.showCart && (
            <Box mb={4}>
              <PromoVoucherSections hook={hook} productData={productData} />
            </Box>
          )}

          <Text fontSize="xl" fontWeight="bold" mb={1}>
            Total
          </Text>
          <PriceRow
            hook={hook}
            cartTotalData={cartTotalData}
            isCartTotalFetching={isCartTotalFetching}
            priceFontSize="xl"
            priceBoxMinH={12}
          />
        </Box>

        {/* Secondary actions — outside the card. Bagikan commented out per Figma 18464:134138. */}
        <HStack spacing={2} mt={1}>
          <VStack
            spacing={1}
            flex={1}
            cursor="pointer"
            onClick={handleAddToWishlist}
            bgColor="#F1F8FF"
            color="#0000A3"
            py={2}
            rounded="md"
            transition="background-color 0.15s ease"
            _hover={{ bgColor: '#D6E4FF' }}
          >
            <Icon as={wishlistId ? BookmarkAdded : BookmarkAdd} boxSize={5} />
            <Text fontSize="xs">Simpan</Text>
          </VStack>
          <VStack
            spacing={1}
            flex={1}
            cursor="pointer"
            onClick={hook.handleDaftarBelanjaClick}
            bgColor="#F1F8FF"
            color="#0000A3"
            py={2}
            rounded="md"
            transition="background-color 0.15s ease"
            _hover={{ bgColor: '#D6E4FF' }}
          >
            <img src="/assets/icons/template-shop.svg" width={20} height={20} alt="" />
            <Text fontSize="xs">Daftar Belanja</Text>
          </VStack>
        </HStack>

        <ContactSalesModal
          isOpen={hook.isContactSalesOpen}
          onClose={hook.onCloseContactSales}
          productId={productId}
          source="detail"
        />
      </Box>
    </Box>
  );
};

export const OrderQuantityCounterMobile = (props: OrderQuantityCounterProps) => {
  const hook = useOrderCounter(props, 'mobile');
  const {
    productId,
    cartTotalData,
    isCartTotalFetching,
    productData,
    isCounterOpen: controlledOpen,
    onOpenCounter,
    onCloseCounter,
  } = props;

  // Controlled by the page (so the drawer can close after a successful add),
  // with a local fallback so the component works standalone / in tests.
  const [localOpen, setLocalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : localOpen;

  const openCounter = () => {
    onOpenCounter?.();
    if (!isControlled) setLocalOpen(true);
  };
  const closeCounter = () => {
    onCloseCounter?.();
    if (!isControlled) setLocalOpen(false);
  };

  // The bottom-bar "+ Keranjang" opens the drawer for in-stock products; for
  // price-zero / contact-sales products it routes straight to the sales flow.
  const handleBottomCart = () => {
    if (hook.showCart) {
      openCounter();
    } else {
      hook.handleKeranjangClick();
    }
  };

  return (
    <>
      {/* Default bottom bar — hidden while the drawer is open. */}
      {!isOpen && (
        <Box
          display={{ base: 'flex', md: 'none' }}
          width="100vw"
          position="fixed"
          left={0}
          bgColor="white"
          bottom="0"
          zIndex={1}
          boxShadow="0px -4px 12px rgba(0, 0, 0, 0.1)"
          px="30px"
          pt="24px"
          pb="24px"
        >
          <HStack w="full" spacing={3}>
            <Button
              flex={1}
              h="48px"
              borderRadius="8px"
              variant="outline"
              borderColor="blue"
              color="blue"
              fontSize="14px"
              fontWeight={500}
              leftIcon={
                <img
                  src="/assets/icons/template-shop.svg"
                  width={20}
                  height={20}
                  alt=""
                />
              }
              onClick={hook.handleDaftarBelanjaClick}
              isDisabled={hook.isAddDisabled}
            >
              Daftar Belanja
            </Button>
            <Button
              flex={1}
              h="48px"
              borderRadius="8px"
              bgColor="blue"
              color="white"
              variant="solid"
              fontSize="14px"
              fontWeight={500}
              onClick={handleBottomCart}
              isDisabled={hook.isAddDisabled}
              isLoading={isCartTotalFetching && !hook.isPriceZero}
            >
              {hook.keranjangLabel}
            </Button>
          </HStack>
        </Box>
      )}

      {/* "Atur Jumlah Pesanan" drawer with screen overlay for focus. */}
      <Drawer
        isOpen={isOpen}
        onClose={closeCounter}
        placement="bottom"
        autoFocus={false}
      >
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl">
          <DrawerHeader fontSize="lg" fontWeight="bold" pb={2}>
            Jumlah Pesanan
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody pb={6}>
            <Flex align="center" justify="space-between" mb={3}>
              <Text color={hook.statusColor} fontWeight="semibold" flex={1} pr={4}>
                {hook.statusLabel}
              </Text>
              {hook.showCart && (
                <Box w="160px" flexShrink={0}>
                  <QuantityControl
                    variant="add-to-cart"
                    hasFractionalStep={hook.hasFractionalStep}
                    inputProps={hook.inputProps}
                    incProps={hook.incProps}
                    decProps={hook.decProps}
                    isLoading={isCartTotalFetching}
                  />
                </Box>
              )}
            </Flex>

            {hook.showCart && (
              <PromoVoucherSections hook={hook} productData={productData} />
            )}

            <Box>
              <Text fontSize="lg" fontWeight="semibold">
                Total
              </Text>
              <PriceRow
                hook={hook}
                cartTotalData={cartTotalData}
                isCartTotalFetching={isCartTotalFetching}
                priceFontSize="2xl"
                priceBoxMinH={10}
                stackOnSmall
              />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <ContactSalesModal
        isOpen={hook.isContactSalesOpen}
        onClose={hook.onCloseContactSales}
        productId={productId}
        source="detail"
      />
    </>
  );
};
